import { Field, Form, useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import closeIcon from '../../images/close.svg';
import { ListQuery } from "../../models/ListQuery";
import { getCertificateTypes } from "../../services/securityPolicyService";
import { requiredValidation, validateCertificate } from "../../utilities/Validation";
import Label from "../label/label";
import PaginatedSearchableSelectField from "../selection-field/paginated-searchable-select-field";
import { CERTIFICATE_IMPORT_EXPORT_TYPES } from "../../constants/certificate-constants";

interface ImportExportCertificateCardProps {
    type: "import" | "export";
    title: string;
    onClose: () => void;
}

export const ImportExportCertificateCard: FC<ImportExportCertificateCardProps> = ({ type, title, onClose }) => {
    const { translate } = useLocale();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

    const handleImportCertificate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        if (file) {
            setSelectedFileName(file.name);
            setFieldValue("certificate", file);
        } else {
            setSelectedFileName(null);
            setFieldValue("certificate", null);
        }
    }

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFieldValue("certificateType", value);
    }

    return (
        <Form style={{ width: '350px' }}>
            <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                <p className="pl-3 font-bold font text-white">{title}</p>
                <img src={closeIcon} onClick={onClose} className="cursor-pointer mr-2" />
            </div>
            <div className="flex flex-col space-y-4 p-4">
                {type === "import" &&
                    <div className="flex flex-col space-y-1">
                        <div className="flex flex-row justify-between" id='importCertificate'>
                            <Label htmlFor="importCertificate"
                                required
                                className="text-xs text-gray-600">{translate.byKey(TranslationKeys.importCertificate)}:</Label>
                            {errors.certificate && (<span className='text-red-600 text-xs mt-1'>{errors.certificate}</span>)}
                        </div>
                        <Field
                            name="certificate"
                            validate={(value) => validateCertificate(value, translate)}
                            hidden />
                        <label htmlFor="importCertificateInputFieldId"
                            className="secondary secondary-enabled py-1 px-5 cursor-pointer bg-portal-blue-primary-shade2 rounded-md text-white text-center">
                            {translate.byKey(TranslationKeys.chooseFile)}
                        </label>
                        <input
                            id="importCertificateInputFieldId"
                            type="file"
                            accept=".pfx"
                            className="hidden"
                            onChange={(event) => handleImportCertificate(event)}
                        />
                        {selectedFileName && <p className="mt-2 text-xs">{selectedFileName}</p>}
                        <div className="flex flex-row justify-between">
                            <Label htmlFor='fileSettingsType'
                                required
                                className="text-xs text-gray-600 mt-1">
                                {translate.byKey(TranslationKeys.certificateType)}:</Label>
                        </div>
                        <PaginatedSearchableSelectField
                            name={translate.byKey(TranslationKeys.type)}
                            key='fileSettingsType'
                            accessor='fileSettingsType'
                            dataMethod={(listParameters?: ListQuery) => getCertificateTypes()}
                            searchAccessor={'typeName'}
                            required={true}
                            onSelect={(_, value) => {
                                setFieldValue('fileSettingTypeName', value?.label);
                                setFieldValue('fileSettingsType', value?.value);
                            }} />

                    </div>
                }
                {type === "export" &&
                    <div className="flex flex-col space-y-1">
                        <Label htmlFor='exportCertType' className='text-xs text-gray-600' required>{translate.byKey(TranslationKeys.certificateType)}</Label>
                        <div className="flex flex-row space-x-4">
                            <div>
                                <Field type="radio" name="certificateType" id="certificateTypePfx"
                                    defaultChecked={true}
                                    value={CERTIFICATE_IMPORT_EXPORT_TYPES.PFX}
                                    onChange={handleRadioChange} />
                                <span className="ml-1">{translate.byKey(TranslationKeys.certificateTypePfx)}</span>
                            </div>
                            <div>
                                <Field type="radio" name="certificateType" id="certificateTypePem"
                                    value={CERTIFICATE_IMPORT_EXPORT_TYPES.PEM}
                                    onChange={handleRadioChange}
                                    label={"PEM"} />
                                <span className="ml-1">{translate.byKey(TranslationKeys.certificateTypePem)}</span>
                            </div>
                        </div>
                    </div>
                }
                <div className="flex flex-col flex-1">
                    <div className="flex flex-row justify-between" id='certificatePassword'>
                        <Label htmlFor='certificatePassword'
                            required={values.certificateType === CERTIFICATE_IMPORT_EXPORT_TYPES.PFX}
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.certificatePassword)}:</Label>
                        {errors.certificatePassword && (<span className='text-red-600 text-xs'>{errors.certificatePassword}</span>)}
                    </div>
                    <Field id='certificatePasswordInputFieldId'
                        type="password"
                        name="certificatePassword"
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={values.certificateType === CERTIFICATE_IMPORT_EXPORT_TYPES.PFX ? (value) => requiredValidation(value, translate) : undefined} />
                </div>
                <div className="flex flex-row space-x-2 justify-end bg-white">
                    <button className="py-1 px-5 secondary secondary-inverse" type="button" onClick={onClose}>
                        {translate.byKey(TranslationKeys.cancel)}
                    </button>
                    <button
                        id='submitBtn'
                        className="secondary secondary-enabled py-1 px-5"
                        type='submit'>
                        {translate.byKey(TranslationKeys.submit)}
                    </button>
                </div>
            </div>
        </Form>
    );
}
